import { useFetcher } from "@/api/utils";
import { AccountInformation, ProductItemInterface, StoreInterface } from "@/assets/interfaces";
import { generateComplementaryHexColor3, hexToRgb, isValidHexCode, reloadPage } from "@/assets/js/utils/functions";
import { checkStoreIsOpen, getItemThumbnail } from "@/assets/js/utils/utils";
import useAnalytics, { PAGE_TYPE } from "@/components/hooks/analytics";
import SEOTags from "@/components/seo-tags";
import { AppBtn } from "@/components/ui/buttons";
import ErrorScreen from "@/components/ui/error-screen";
import CartContextV2 from "@/contexts/cart-context-v2";
import { useEffect, useState } from "react";
import ProductDetailsSection from "../items/product-details-section";
import ProductPageHeader from "@/components/products/header";
import Portal from "@/components/portal";
import { Cart } from "../sections";
import { GetStoreBankAccount } from "@/api/stores";

export interface ProductPageProps {
  item: Omit<ProductItemInterface, "store"> & { store: StoreInterface };
  error: boolean;
  query?: { [key: string]: string };
}

const ProductPageV2: React.FC<ProductPageProps> = ({ item, error, query }) => {
  const { saveAnalytics } = useAnalytics(PAGE_TYPE.ITEM, query?.ref);
  const store = error ? null : item?.store;
  const storeId = error ? null : store?.id;
  const [currentImage, setCurrentImage] = useState(0);
  const getStoreBankAccountReq = useFetcher(GetStoreBankAccount, { id: storeId });
  const storeBankAccount: AccountInformation = getStoreBankAccountReq?.response?.data;
  const storeIs = checkStoreIsOpen(store);
  const itemOnly = item as unknown as ProductItemInterface;

  useEffect(() => {
    if (!error) {
      saveAnalytics({ store: storeId, item: item.id, itemName: item.name });
      if (item.store) {
        window.COUNTRY = item.store.country;

        if (store?.configuration?.color && isValidHexCode(store?.configuration?.color)) {
          const p = hexToRgb(store?.configuration?.color);
          const primaryRGB = `${p.r}, ${p.g}, ${p.b}`;
          document.documentElement.style.setProperty("--primary-500", primaryRGB);
          document.documentElement.style.setProperty("--discount-badge", primaryRGB);

          const s = hexToRgb(generateComplementaryHexColor3(store?.configuration?.color));
          const secondaryRGB = `${s.r}, ${s.g}, ${s.b}`;
          document.documentElement.style.setProperty("--accent-yellow-500", secondaryRGB);
          document.documentElement.style.setProperty("--add-to-cart", secondaryRGB);
          document.documentElement.style.setProperty("--success", secondaryRGB);
          document.documentElement.style.setProperty("--cart-badge", secondaryRGB);
        }
      }
    }
  }, []);

  if (error) {
    return (
      <ErrorScreen
        title="Something went wrong :("
        message="We couldn't load this item, check your network and try reloading the page"
      >
        <AppBtn className="mt-5" size="md" onClick={reloadPage}>
          Reload page
        </AppBtn>
      </ErrorScreen>
    );
  }

  return (
    <>
      <SEOTags
        tags={{
          title: `${item.name} | ${item.store.name} - Catlog`,
          description: item.description,
          image: getItemThumbnail(item),
          pageUrl: `/products/${item.slug}`,
        }}
      />
      <CartContextV2.Provider
        initialState={{
          storeId: storeId,
          cartRef: `p-${item.id}`,
          account: storeBankAccount,
          currencySettings: store?.currencies
            ? {
                active: store?.currencies?.storefront_default,
                rates: store?.currencies?.rates,
                options: store?.currencies?.storefront,
                defaultCurrency: store?.currencies?.products,
                allRates: store["meta"]?.["rates"]?.rates ?? {},
                rateId: store["meta"]?.["rates"]?.id ?? "",
                markups: store?.currencies?.rates,
              }
            : undefined,
        }}
      >
        <div className="relative md:h-auto md:overflow-hidden justify-end flex flex-col">
          <ProductPageHeader store={store} />
          <div className="store-container mt-17.5 sm:mt-20">
            <ProductDetailsSection item={itemOnly} productOnly store={store} />
          </div>
        </div>
        {item?.images && (
          <Portal>
            <Cart storeIsOpen={storeIs.open} phoneNumber={store.phone} store={store} />
          </Portal>
        )}
      </CartContextV2.Provider>
    </>
  );
};

export default ProductPageV2;
